import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Loader2 } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaBalanceQueryKey,
  getGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaCurrentQueryKey,
  getGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaQueryKey,
  useGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequota,
} from '@/api/generated/company-user/company-user'
import {
  useGetApiCompaniesCurrent,
  usePatchApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequota,
  usePostApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequota,
} from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, TimeQuotaModel } from '@/api/model'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { notify } from '@/lib/notify'
import {
  RegionalHolidays,
  defaultRegionalHolidays,
  HOLIDAY_FIELDS,
  type RegionalHolidaysState,
} from '../../absence/components/RegionalHolidays'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceModuleId: number
  employee: EmployeeUserResponse
}

/** Hours per weekday (in the form; in the request → minutes). */
type WorkdaysState = Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  number
>

const defaultWorkdays: WorkdaysState = {
  monday: 8,
  tuesday: 8,
  wednesday: 8,
  thursday: 8,
  friday: 8,
  saturday: 0,
  sunday: 0,
}

const WEEKDAY_KEYS: (keyof WorkdaysState)[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
]

/** Unwrap the query response from the axios wrapper if needed (hooks return the full response). */
function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

const toHours = (minutes: number) => Math.round((minutes / 60) * 100) / 100

// --- ISO calendar-week helpers (ported from the old portal) ---
function getIsoWeek(date: Date): number {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNumber = target.getUTCDay() || 7
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber)
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1))
  return Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
}

function getIsoWeekStart(week: number, year: number): Date {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7))
  const dayOfWeek = simple.getUTCDay()
  const isoWeekStart = new Date(simple)
  if (dayOfWeek <= 4 && dayOfWeek > 0) {
    isoWeekStart.setUTCDate(simple.getUTCDate() - dayOfWeek + 1)
  } else {
    isoWeekStart.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek)
  }
  return isoWeekStart
}

function getIsoWeekCount(year: number): number {
  return getIsoWeek(new Date(Date.UTC(year, 11, 28)))
}

/** Copy a quota's holiday values into the holidays state. */
function holidaysFromQuota(quota: TimeQuotaModel): RegionalHolidaysState {
  return Object.fromEntries(
    HOLIDAY_FIELDS.map((h) => [h.key, (quota as any)[h.key] ?? defaultRegionalHolidays[h.key]]),
  ) as RegionalHolidaysState
}

/** Copy a quota's workdays (hours/day) into the form state. */
function workdaysFromQuota(quota: TimeQuotaModel): WorkdaysState {
  return {
    monday: toHours(quota.minutesMonday ?? 0),
    tuesday: toHours(quota.minutesTuesday ?? 0),
    wednesday: toHours(quota.minutesWednesday ?? 0),
    thursday: toHours(quota.minutesThursday ?? 0),
    friday: toHours(quota.minutesFriday ?? 0),
    saturday: toHours(quota.minutesSaturday ?? 0),
    sunday: toHours(quota.minutesSunday ?? 0),
  }
}

/** Workdays → minute fields of the request. */
function workdaysToMinutes(w: WorkdaysState) {
  return {
    minutesMonday: Math.round(w.monday * 60),
    minutesTuesday: Math.round(w.tuesday * 60),
    minutesWednesday: Math.round(w.wednesday * 60),
    minutesThursday: Math.round(w.thursday * 60),
    minutesFriday: Math.round(w.friday * 60),
    minutesSaturday: Math.round(w.saturday * 60),
    minutesSunday: Math.round(w.sunday * 60),
  }
}

const YEAR_NOW = new Date().getUTCFullYear()

/**
 * Work-time quota dialog (clock button of the overview). Shows the existing
 * quota of the current year, allows creating a new quota over a calendar-week
 * range (start/end as year + calendar week) and editing an existing quota from a
 * chosen calendar week onward (applies the change to that week and all following
 * weeks). Ported from the old portal's `WorkTimeQuotaDialog` to React Query /
 * shadcn; role gating (canEdit) is still missing in the new portal →
 * editing/creating is currently always allowed.
 */
export function WorkTimeQuotaDialog({ open, onOpenChange, serviceModuleId, employee }: Props) {
  const { t } = useTranslation(['service-modules-timetracking', 'service-modules', 'common', 'filters'])
  const weekdayLabels = t('dates.weekdaysShort', { ns: 'common', returnObjects: true }) as string[]
  const queryClient = useQueryClient()
  const employeeId = employee.id ?? ''

  // Create
  const [initialSurplusHours, setInitialSurplusHours] = useState('')
  const [startYear, setStartYear] = useState(YEAR_NOW)
  const [startWeek, setStartWeek] = useState<number | ''>('')
  const [endYear, setEndYear] = useState(YEAR_NOW)
  const [endWeek, setEndWeek] = useState<number | ''>('')
  const [workdays, setWorkdays] = useState<WorkdaysState>(defaultWorkdays)
  const [regionalHolidays, setRegionalHolidays] = useState<RegionalHolidaysState>(defaultRegionalHolidays)
  const [pausesAreWorkingTime, setPausesAreWorkingTime] = useState(false)
  // Edit
  const [editQuotaId, setEditQuotaId] = useState<number | ''>('')
  const [editWorkdays, setEditWorkdays] = useState<WorkdaysState>(defaultWorkdays)
  const [editRegionalHolidays, setEditRegionalHolidays] = useState<RegionalHolidaysState>(defaultRegionalHolidays)
  const [editPausesAreWorkingTime, setEditPausesAreWorkingTime] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset the fields on every open (or employee change).
  useEffect(() => {
    if (!open) return
    setInitialSurplusHours('')
    setStartYear(YEAR_NOW)
    setStartWeek(getIsoWeek(new Date()))
    setEndYear(YEAR_NOW)
    setEndWeek(getIsoWeekCount(YEAR_NOW))
    setWorkdays(defaultWorkdays)
    setRegionalHolidays(defaultRegionalHolidays)
    setPausesAreWorkingTime(false)
    setEditQuotaId('')
    setEditWorkdays(defaultWorkdays)
    setEditRegionalHolidays(defaultRegionalHolidays)
    setEditPausesAreWorkingTime(false)
    setError(null)
  }, [open, employeeId])

  const quotasQuery = useGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequota(
    serviceModuleId,
    { employeeId },
    { query: { enabled: open && !!employeeId } },
  )
  const quotas = useMemo(() => unwrap<TimeQuotaModel[]>(quotasQuery.data) ?? [], [quotasQuery.data])

  const companyQuery = useGetApiCompaniesCurrent()
  const companyId = (unwrap<{ id?: number }>(companyQuery.data))?.id ?? null

  const create = usePostApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequota()
  const update = usePatchApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequota()
  const isSubmitting = create.isPending || update.isPending

  const hasExistingQuotas = quotas.length > 0

  /** Quotas sorted by year + calendar week (for display + "from CW" editing). */
  const sortedQuotas = useMemo(
    () =>
      [...quotas].sort((a, b) => (a.year !== b.year ? a.year - b.year : a.calendarWeek - b.calendarWeek)),
    [quotas],
  )
  const firstQuota = sortedQuotas[0]
  const lastQuota = sortedQuotas[sortedQuotas.length - 1]

  const startWeekOptions = useMemo(
    () => Array.from({ length: getIsoWeekCount(startYear) }, (_, i) => i + 1),
    [startYear],
  )
  const endWeekOptions = useMemo(
    () => Array.from({ length: getIsoWeekCount(endYear) }, (_, i) => i + 1),
    [endYear],
  )
  const yearOptions = [YEAR_NOW - 1, YEAR_NOW, YEAR_NOW + 1, YEAR_NOW + 2]

  // Selection in the "edit" dropdown → fill the form with the quota's values.
  function handleEditSelection(quota: TimeQuotaModel) {
    setEditQuotaId(quota.quotaId)
    setEditWorkdays(workdaysFromQuota(quota))
    setEditRegionalHolidays(holidaysFromQuota(quota))
    setEditPausesAreWorkingTime(quota.pausesAreWorkingTime ?? false)
  }

  async function invalidate() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaQueryKey(serviceModuleId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaCurrentQueryKey(serviceModuleId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequotaBalanceQueryKey(serviceModuleId),
      }),
    ])
  }

  async function handleCreate() {
    setError(null)
    if (startWeek === '' || endWeek === '' || endYear < startYear || (endYear === startYear && endWeek < startWeek)) {
      setError(t('timetracking.quota.errors.invalidRange'))
      return
    }
    if (companyId === null) {
      setError(t('shared.quota.companyError', { ns: 'service-modules' }))
      return
    }

    let initialSurplusMinutes: number | undefined
    if (!hasExistingQuotas) {
      const hours = Number(initialSurplusHours)
      if (initialSurplusHours === '' || Number.isNaN(hours)) {
        setError(t('timetracking.quota.errors.missingInitialSurplus'))
        return
      }
      initialSurplusMinutes = Math.round(hours * 60)
    }

    const start = getIsoWeekStart(startWeek, startYear)
    const end = getIsoWeekStart(endWeek, endYear)
    end.setUTCDate(end.getUTCDate() + 6)

    try {
      await create.mutateAsync({
        serviceModuleId,
        data: {
          employeeUserId: employeeId,
          companyId,
          createFrom: dayjs(start).format('YYYY-MM-DD'),
          createUntil: dayjs(end).format('YYYY-MM-DD'),
          ...workdaysToMinutes(workdays),
          ...regionalHolidays,
          pausesAreWorkingTime,
          ...(initialSurplusMinutes !== undefined ? { initialSurplusMinutes } : {}),
        },
      })
      await invalidate()
      notify.success(t('timetracking.quota.created'))
      onOpenChange(false)
    } catch (err) {
      notify.apiError(err, t('timetracking.quota.createError'))
    }
  }

  async function handleUpdate() {
    setError(null)
    if (editQuotaId === '') {
      setError(t('timetracking.quota.errors.selectQuota'))
      return
    }
    const selectedIndex = sortedQuotas.findIndex((q) => q.quotaId === editQuotaId)
    if (selectedIndex < 0) {
      setError(t('timetracking.quota.errors.invalidQuota'))
      return
    }

    // Apply the change to the selected quota and all following weeks.
    const quotasToUpdate = sortedQuotas.slice(selectedIndex)
    const body = {
      ...workdaysToMinutes(editWorkdays),
      ...editRegionalHolidays,
      pausesAreWorkingTime: editPausesAreWorkingTime,
    }

    try {
      for (const quota of quotasToUpdate) {
        await update.mutateAsync({
          serviceModuleId,
          data: body,
          params: { id: quota.quotaId, employeeId },
        })
      }
      await invalidate()
      notify.success(t('timetracking.quota.updated'))
      onOpenChange(false)
    } catch (err) {
      notify.apiError(err, t('timetracking.quota.updateError'))
    }
  }

  const employeeName = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || t('dropdown.employeeFallback', { ns: 'filters' })

  /** Weekday editor (hours per day). */
  const workdaysEditor = (value: WorkdaysState, onChange: (v: WorkdaysState) => void) => (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{t('timetracking.quota.workHoursPerDay')}</span>
      <div className="flex flex-wrap gap-2">
        {WEEKDAY_KEYS.map((key, index) => (
          <div key={key} className="flex w-16 flex-col gap-1">
            <Label className="text-xs text-muted-foreground">{weekdayLabels[index]}</Label>
            <Input
              type="number"
              min={0}
              step={0.25}
              value={value[key]}
              onChange={(e) => onChange({ ...value, [key]: Number(e.target.value) })}
            />
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('timetracking.quota.title', { name: employeeName })}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {quotasQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('shared.quota.loading', { ns: 'service-modules' })}
            </div>
          ) : (
            <>
              {hasExistingQuotas && firstQuota && lastQuota ? (
                <div className="flex flex-col gap-1 rounded-lg border bg-muted/30 p-3 text-sm">
                  <span className="font-semibold">{t('timetracking.quota.existing')}</span>
                  <span>
                    {t('timetracking.quota.fromWeek', { week: firstQuota.calendarWeek, year: firstQuota.year, hours: toHours(firstQuota.plannedWorkMinutes ?? 0) })}
                  </span>
                  <span className="text-muted-foreground">
                    {t('timetracking.quota.untilWeek', { week: lastQuota.calendarWeek, year: lastQuota.year, hours: toHours(lastQuota.plannedWorkMinutes ?? 0) })}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('timetracking.quota.none')}</p>
              )}

              {/* --- Edit (only for existing quotas) --- */}
              {hasExistingQuotas && (
                <div className="flex flex-col gap-3 border-t pt-4">
                  <span className="text-sm font-semibold">{t('timetracking.quota.editTitle')}</span>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('timetracking.quota.fromCalendarWeek')}</Label>
                    <Select
                      value={editQuotaId !== '' ? String(editQuotaId) : undefined}
                      onValueChange={(v) => {
                        const quota = sortedQuotas.find((q) => q.quotaId === Number(v))
                        if (quota) handleEditSelection(quota)
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('timetracking.quota.selectWeekPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {sortedQuotas.map((quota) => (
                          <SelectItem key={quota.quotaId} value={String(quota.quotaId)}>
                            {t('timetracking.quota.week', { week: quota.calendarWeek, year: quota.year })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">
                      {t('timetracking.quota.editHint')}
                    </span>
                  </div>

                  {editQuotaId !== '' && (
                    <>
                      {workdaysEditor(editWorkdays, setEditWorkdays)}
                      <RegionalHolidays value={editRegionalHolidays} onChange={setEditRegionalHolidays} />
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox
                          checked={editPausesAreWorkingTime}
                          onCheckedChange={(c) => setEditPausesAreWorkingTime(c === true)}
                        />
                        {t('shared.quota.pausesAreWorkingTime', { ns: 'service-modules' })}
                      </label>
                      <Button onClick={handleUpdate} disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                        {t('timetracking.quota.saveEdit')}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* --- Create --- */}
              <div className="flex flex-col gap-4 border-t pt-4">
                <span className="text-sm font-semibold">{t('timetracking.quota.createTitle')}</span>

                {!hasExistingQuotas && (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="tt-initial-surplus">{t('timetracking.quota.initialSurplus')}</Label>
                    <Input
                      id="tt-initial-surplus"
                      type="number"
                      step={0.25}
                      value={initialSurplusHours}
                      onChange={(e) => setInitialSurplusHours(e.target.value)}
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('timetracking.quota.fromYear')}</Label>
                    <Select
                      value={String(startYear)}
                      onValueChange={(v) => {
                        const y = Number(v)
                        setStartYear(y)
                        if (endYear < y) setEndYear(y)
                      }}
                    >
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('timetracking.quota.fromWeekLabel')}</Label>
                    <Select
                      value={startWeek !== '' ? String(startWeek) : undefined}
                      onValueChange={(v) => setStartWeek(Number(v))}
                    >
                      <SelectTrigger className="w-full"><SelectValue placeholder={t('timetracking.quota.weekPlaceholder')} /></SelectTrigger>
                      <SelectContent>
                        {startWeekOptions.map((w) => <SelectItem key={w} value={String(w)}>{t('timetracking.quota.weekOption', { week: w })}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('timetracking.quota.untilYear')}</Label>
                    <Select
                      value={String(endYear)}
                      onValueChange={(v) => {
                        const y = Number(v)
                        setEndYear(y)
                        if (startYear > y) setStartYear(y)
                      }}
                    >
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('timetracking.quota.untilWeekLabel')}</Label>
                    <Select
                      value={endWeek !== '' ? String(endWeek) : undefined}
                      onValueChange={(v) => setEndWeek(Number(v))}
                    >
                      <SelectTrigger className="w-full"><SelectValue placeholder={t('timetracking.quota.weekPlaceholder')} /></SelectTrigger>
                      <SelectContent>
                        {endWeekOptions
                          .filter((w) => (endYear > startYear ? true : startWeek === '' || w >= startWeek))
                          .map((w) => <SelectItem key={w} value={String(w)}>{t('timetracking.quota.weekOption', { week: w })}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {workdaysEditor(workdays, setWorkdays)}
                <RegionalHolidays value={regionalHolidays} onChange={setRegionalHolidays} />
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={pausesAreWorkingTime}
                    onCheckedChange={(c) => setPausesAreWorkingTime(c === true)}
                  />
                  {t('shared.quota.pausesAreWorkingTime', { ns: 'service-modules' })}
                </label>
                <Button onClick={handleCreate} disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                  {t('timetracking.quota.create')}
                </Button>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('buttons.close', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
